// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './OrderHistory.scss';
// Component imports:
import Table from 'react-bootstrap/Table';
// Service imports:
import { formatDate, formatNumber } from '../../services/utils';
import { sortTickData } from '../../services/sortUtils';
// Action imports:
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { BY_LEVEL_SYMBOLS, WithBook } from '../../types/api/Types';
import { PrivateOrderTickData, PrivateTradeTickData } from '../../types/api/PrivateTypes';
import { PublicOrderTickData } from '../../types/api/PublicTypes';

interface IOwnProps {
    publicOrders: WithBook<PublicOrderTickData>[],
    privateOrders: PrivateOrderTickData[]
    privateTrades: PrivateTradeTickData[]
    symbol: string
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;

class OrderHistory extends React.Component<Props> {
    getOrderHistoryRows = () => {
        const { selectedTick, publicOrders, privateOrders, privateTrades, symbol } = this.props;

        if (selectedTick?.tickdata_type !== 'Order' && selectedTick?.tickdata_type !== 'PrivateOrder' && selectedTick?.tickdata_type !== 'PrivateTrade') {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={6}>
                        Click an Order to see its History. 
                        <br/>
                        {(BY_LEVEL_SYMBOLS.includes(symbol)) && 'Note: For Market-by-Level, it is only possible with Private Orders.'}
                    </td>
                </tr>
            );
        }

        // Declare important sets for finding orders. Using sets because my homies hate duplicates 😎.
        const marketFeedIds = new Set<string>();
        const privateOrderIds = new Set<string>();

        // If public order, go through some extra steps.
        if (selectedTick.tickdata_type !== 'PrivateOrder') {
            if (selectedTick.order_id !== null) marketFeedIds.add(selectedTick.order_id);
            // Find private orders with market feed id for this order.
            const privateOrdersWithThisMarketFeedId = privateOrders.filter(privateOrder => privateOrder.market_feed_id === selectedTick.order_id);
            // Get id's of private orders.
            for (let privateOrder of privateOrdersWithThisMarketFeedId) {
                privateOrderIds.add(privateOrder.id);
            }
        } else {
            // Add selected private order's id to set.
            privateOrderIds.add(selectedTick.id);
        }

        // Find all private orders that match this private order.
        const privateOrdersWithSameId = privateOrders.filter(privateOrder => privateOrderIds.has(privateOrder.id));

        // Get all market feed ids of all private orders.
        for (let privateOrderWithSameId of privateOrdersWithSameId) {
            if (privateOrderWithSameId.market_feed_id !== null) {
                marketFeedIds.add(privateOrderWithSameId.market_feed_id);
            }
        }

        // ----------------------------- Public Orders -----------------------------
        // Get all public orders with these market feed ids.
        const publicOrdersThatAreReferencedByPrivateOrders = publicOrders
            .map(bookTick => bookTick.tick)
            .filter(publicOrder => (publicOrder.order_id !== null && marketFeedIds.has(publicOrder.order_id)
        ));

        // ----------------------------- Private Orders -----------------------------
        // Get all private orders that reference these market feed ids.
        const privateOrdersThatReferenceTheMarketFeedIds = privateOrders.filter(privateOrder => (
            privateOrder.market_feed_id !== null
            && marketFeedIds.has(privateOrder.market_feed_id)
        ));
        // Get all private orders that are in private trades.
        const privateOrdersThatAreInPrivateTrades = privateOrders.filter(privateOrder => (
            privateOrder.id !== null &&  marketFeedIds.has(privateOrder.id)
        ));

        // ----------------------------- Private Trades -----------------------------
        // Get all private trades that reference these market feed ids.
        const privateTradesThatReferenceTheMarketFeedIds = privateTrades.filter(privateTrade => (
            privateTrade.market_feed_id !== null
            && marketFeedIds.has(privateTrade.market_feed_id)
        ));

        // ----------------------------- Display list -----------------------------
        // Congregate arrays of all things that will be displayed together.
        const ordersAndTrades = [
            ...publicOrdersThatAreReferencedByPrivateOrders,
            ...privateOrdersThatReferenceTheMarketFeedIds,
            ...privateOrdersThatAreInPrivateTrades,
            ...privateTradesThatReferenceTheMarketFeedIds
        ];

        const sortedOrdersAndTrades = sortTickData(ordersAndTrades);
        
        const jsxRows = sortedOrdersAndTrades.map((orderOrTrade, index) => (
            <tr
                key={index}
                className={(orderOrTrade === selectedTick) ? 'current' : undefined}
                onClick={() => this.props.setSelectedTick(orderOrTrade)}
            >
                <td>{formatDate(orderOrTrade.timestamp, true, true)}</td>
                <td>{orderOrTrade.tickdata_type}</td>
                <td>{orderOrTrade.update_code}</td>
                <td>{orderOrTrade.side}</td>
                <td>{formatNumber(orderOrTrade.quantity, 'volume')}</td>
                <td>{formatNumber(orderOrTrade.price || null)}</td>
            </tr>
        ));

        if (jsxRows.length === 0) {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={6}>
                        No Order History
                    </td>
                </tr>
            )
        }

        return jsxRows;
    }

    render () {
        const { selectedTick } = this.props;
        return (
            <div id='OrderHistory'>
                <h5 className='SectionTitle'>
                    {selectedTick?.tickdata_type === 'PrivateTrade' ? "Order's in Trade" : "Order History"}
                    {
                        (selectedTick?.tickdata_type !== 'Order' && selectedTick?.tickdata_type !== 'PrivateOrder')
                        ? null
                        : (
                            <span>
                                {' '}(ID: {(selectedTick?.tickdata_type === 'PrivateOrder') ? selectedTick.market_feed_id : selectedTick?.order_id})
                            </span>
                        )
                    }
                </h5>
                <Table className='CompressedTable ClickableRows'>
                    <thead>
                        <tr>
                            <th scope='col'>Timestamp</th>
                            <th scope='col'>Type</th>
                            <th scope='col'>Code</th>
                            <th scope='col'>Side</th>
                            <th scope='col'>Volume</th>
                            <th scope='col'>Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.getOrderHistoryRows()}
                    </tbody>
                </Table>
            </div>
        );
    }
}

function mapStateToProps({ dashboardReducer }: RootState) {
    const { selectedTick } = dashboardReducer;
    return {
        selectedTick
    };
}

const connector = connect(mapStateToProps, { setSelectedTick });
export default connector(OrderHistory);
